import { openDB, DBSchema, IDBPDatabase } from "idb";
import { ScreenData } from "../types/ScreenTypes";

// Define the DB schema with strong typing
interface ScreenDBSchema extends DBSchema {
  screens: {
    key: string; // screenCode
    value: {
      data: ScreenData;
      timestamp: number; // For cache invalidation
    };
  };
}

class ScreenDatabase {
  private dbPromise: Promise<IDBPDatabase<ScreenDBSchema>> | null = null;

  // Azure Best Practice: Initialize DB with proper versioning
  private initDB(): Promise<IDBPDatabase<ScreenDBSchema>> {
    return openDB<ScreenDBSchema>("M247ScreensDB", 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("screens")) {
          db.createObjectStore("screens");
        }
      },
    });
  }

  private async getDB(): Promise<IDBPDatabase<ScreenDBSchema>> {
    if (!this.dbPromise) {
      this.dbPromise = this.initDB();
    }
    try {
      return await this.dbPromise;
    } catch (error) {
      console.warn("IndexedDB open failed, attempting recovery:", error);
      this.dbPromise = null;
      try {
        await new Promise<void>((resolve, reject) => {
          const req = indexedDB.deleteDatabase("M247ScreensDB");
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
          req.onblocked = () => resolve();
        });
        console.warn("Corrupted M247ScreensDB deleted, recreating");
      } catch (deleteErr) {
        console.error("Failed to delete corrupted DB:", deleteErr);
      }
      this.dbPromise = this.initDB();
      return this.dbPromise;
    }
  }

  async getScreenData(screenCode: string): Promise<ScreenData | null> {
    try {
      const db = await this.getDB();
      const data = await db.get("screens", screenCode);
      return data ? data.data : null;
    } catch (error) {
      console.error("Error getting screen data from IndexedDB:", error);
      return null;
    }
  }

  async saveScreenData(screenCode: string, data: ScreenData): Promise<void> {
    try {
      const db = await this.getDB();
      await db.put(
        "screens",
        {
          data,
          timestamp: Date.now(),
        },
        screenCode,
      );
      console.log("Screen data saved to IndexedDB");
    } catch (error) {
      console.error("Error saving screen data to IndexedDB:", error);
    }
  }

  // Azure Best Practice: Cache invalidation - check if data is stale
  async isDataStale(
    screenCode: string,
    maxAgeMs: number = 24 * 60 * 60 * 1000,
  ): Promise<boolean> {
    try {
      const db = await this.getDB();
      const data = await db.get("screens", screenCode);
      if (!data) return true;

      const now = Date.now();
      return now - data.timestamp > maxAgeMs;
    } catch (error) {
      console.error("Error checking data freshness:", error);
      return true; // Assume stale on error
    }
  }
}

export const screenDB = new ScreenDatabase();
